import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CourseCard } from "@/components/CourseCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ANY = "__any__";

export default function Courses() {
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState({ cities: [], specialties: [], states: [] });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");

  const city = sp.get("city") || "";
  const specialty = sp.get("specialty") || "";
  const format = sp.get("format") || "";

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    ["city", "specialty", "format", "q"].forEach((k) => { const v = sp.get(k); if (v) params[k] = v; });
    try {
      const { data } = await api.get("/courses", { params });
      setCourses(data);
    } finally { setLoading(false); }
  }, [sp]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val && val !== ANY) next.set(key, val); else next.delete(key);
    setSp(next);
  };

  const onSearch = (e) => { e.preventDefault(); setFilter("q", q); };
  const clearAll = () => { setSp(new URLSearchParams()); setQ(""); };

  const hasFilters = city || specialty || format || sp.get("q");

  return (
    <div>
      <Navbar />
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Explore Courses</h1>
          <p className="mt-2 text-muted-foreground">Filter by city, specialty and format to find your match.</p>
          <form onSubmit={onSearch} className="mt-6 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="courses-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search e.g. emergency medicine, cardiology, dialysis..." className="pl-9" />
            </div>
            <Button type="submit" data-testid="courses-search-btn">Search</Button>
          </form>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[260px_1fr] lg:px-8">
        {/* Filters */}
        <aside className="h-fit rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <span className="flex items-center gap-2 font-semibold"><SlidersHorizontal className="h-4 w-4" /> Filters</span>
            {hasFilters && (
              <button data-testid="clear-filters-btn" onClick={clearAll} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive">
                <X className="h-3.5 w-3.5" /> Clear
              </button>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">City</label>
              <Select value={city || ANY} onValueChange={(v) => setFilter("city", v)}>
                <SelectTrigger data-testid="filter-city"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any city</SelectItem>
                  {meta.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Specialty</label>
              <Select value={specialty || ANY} onValueChange={(v) => setFilter("specialty", v)}>
                <SelectTrigger data-testid="filter-specialty"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any specialty</SelectItem>
                  {meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Format</label>
              <Select value={format || ANY} onValueChange={(v) => setFilter("format", v)}>
                <SelectTrigger data-testid="filter-format"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any format</SelectItem>
                  {["Offline", "Hybrid", "Online"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </aside>

        {/* Results */}
        <main>
          <p data-testid="results-count" className="mb-4 text-sm text-muted-foreground">
            {loading ? "Loading..." : `${courses.length} course${courses.length !== 1 ? "s" : ""} found`}
          </p>
          {!loading && courses.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
              <p className="font-heading text-lg font-semibold">No courses match your filters</p>
              <p className="mt-1 text-sm text-muted-foreground">Try widening your search or clearing filters.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {courses.map((c) => <CourseCard key={c.id} course={c} />)}
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}
